import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from 'rxjs/operators';
import { GlobalSearchService } from './global-search.service';
import { SearchResult } from './search-catalog';

interface IndexedResult {
  result: SearchResult;
  index: number;
}

interface ResultGroup {
  label: string;
  items: IndexedResult[];
}

export interface GlobalSearchDialogData {
  organizationId: number | null;
}

@Component({
  selector: 'app-global-search-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './global-search-dialog.component.html',
  styleUrl: './global-search-dialog.component.scss',
})
export class GlobalSearchDialogComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  searchControl = new FormControl('');

  // Stato dei risultati per sezione (signals -> change detection zoneless).
  pages = signal<SearchResult[]>([]);
  actions = signal<SearchResult[]>([]);
  invoices = signal<SearchResult[]>([]);
  companies = signal<SearchResult[]>([]);
  loading = signal(false);
  activeIndex = signal(0);

  private readonly orgId: number | null;

  /** Gruppi non vuoti con indice globale progressivo per la navigazione. */
  groups = computed<ResultGroup[]>(() => {
    let idx = 0;
    const build = (label: string, arr: SearchResult[]): ResultGroup => ({
      label,
      items: arr.map((result) => ({ result, index: idx++ })),
    });

    const out: ResultGroup[] = [];
    if (this.pages().length) out.push(build('PAGINE', this.pages()));
    if (this.actions().length) out.push(build('AZIONI', this.actions()));
    if (this.invoices().length) out.push(build('FATTURE', this.invoices()));
    if (this.companies().length) out.push(build('CLIENTI', this.companies()));
    return out;
  });

  /** Lista piatta, allineata agli indici globali di `groups`. */
  flatResults = computed<SearchResult[]>(() =>
    this.groups().flatMap((g) => g.items.map((i) => i.result)),
  );

  hasQuery = computed(() => (this.searchControl.value ?? '').trim().length > 0);

  hasResults = computed(() => this.flatResults().length > 0);

  /** Ref presente solo su desktop (dialog classico). */
  private dialogRef = inject(MatDialogRef<GlobalSearchDialogComponent>, {
    optional: true,
  });
  /** Ref presente solo su mobile (bottom sheet). */
  private bottomSheetRef = inject(
    MatBottomSheetRef<GlobalSearchDialogComponent>,
    { optional: true },
  );

  constructor(private searchService: GlobalSearchService) {
    const dialogData = inject(MAT_DIALOG_DATA, {
      optional: true,
    }) as GlobalSearchDialogData | null;
    const bottomSheetData = inject(MAT_BOTTOM_SHEET_DATA, {
      optional: true,
    }) as GlobalSearchDialogData | null;
    this.orgId = (dialogData ?? bottomSheetData)?.organizationId ?? null;
  }

  ngOnInit(): void {
    // Suggerimenti iniziali (pagine + azioni) a palette vuota.
    this.applyStatic('');

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term) => {
          const t = (term ?? '').trim();
          this.applyStatic(t);
          this.activeIndex.set(0);
          this.loading.set(t.length > 0);
        }),
        switchMap((term) => {
          const t = (term ?? '').trim();
          if (!t) {
            return of({ invoices: [], companies: [] });
          }
          return this.searchService
            .searchRemote(t, this.orgId)
            .pipe(catchError(() => of({ invoices: [], companies: [] })));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((remote) => {
        this.invoices.set(remote.invoices);
        this.companies.set(remote.companies);
        this.loading.set(false);
        this.clampActiveIndex();
      });
  }

  private applyStatic(term: string): void {
    const { pages, actions } = this.searchService.searchStatic(term, this.orgId);
    this.pages.set(pages);
    this.actions.set(actions);
    // In stato vuoto non mostriamo fatture/clienti.
    if (!term) {
      this.invoices.set([]);
      this.companies.set([]);
    }
    this.clampActiveIndex();
  }

  private clampActiveIndex(): void {
    const max = this.flatResults().length - 1;
    if (this.activeIndex() > max) {
      this.activeIndex.set(Math.max(0, max));
    }
  }

  onKeydown(event: KeyboardEvent): void {
    const total = this.flatResults().length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (total) this.activeIndex.set((this.activeIndex() + 1) % total);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (total) this.activeIndex.set((this.activeIndex() - 1 + total) % total);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const current = this.flatResults()[this.activeIndex()];
      if (current) this.execute(current);
    }
    // Esc è gestito di default da MatDialog/MatBottomSheet.
  }

  execute(result: SearchResult): void {
    result.action();
    this.close();
  }

  private close(): void {
    this.dialogRef?.close();
    this.bottomSheetRef?.dismiss();
  }

  setActive(index: number): void {
    this.activeIndex.set(index);
  }

  clear(): void {
    this.searchControl.setValue('');
  }
}
